import { Component, Input, OnInit, OnDestroy, Output, EventEmitter, ViewChild, TemplateRef } from '@angular/core';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UpSellingMessage } from './upselling-message';
import { ModalContactoSoporteComponent } from 'src/app/shared/contacto-soporte/contacto-soporte.component';
import { SuscripcionesInformacionPlanesComponent } from 'src/app/components/suscripciones/suscripciones-informacion-planes/suscripciones-informacion-planes.component';
import { AlertService, LoadingService } from 'ngx-kuv-tools';
import { SuscripcionesService } from 'src/app/components/suscripciones/suscripciones.service';
import { SentryService } from '../sentry/sentry.service';
import { UpSellingMessagesService } from './upselling-messages.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-upselling-messages',
  templateUrl: './upselling-messages.component.html',
  styleUrls: ['./upselling-messages.component.scss']
})
export class UpSellingMessagesComponent implements OnInit, OnDestroy {
  @Input() message: UpSellingMessage | null = null;
  @Output() acceptPromotion = new EventEmitter<UpSellingMessage>();
  @Output() learnMore = new EventEmitter<UpSellingMessage>();
  @ViewChild('upsellingModal') modalTemplate!: TemplateRef<any>;

  private modalRef: NgbModalRef | null = null;
  private countdownInterval: any;
  imageUrl?: SafeUrl | string;
  constructor(
    private modalService: NgbModal,
    public activeModal: NgbActiveModal,
    private service: SuscripcionesService,
    private loading: LoadingService,
    private alerts: AlertService,
    private sentryService: SentryService,
    private upSellingService: UpSellingMessagesService,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    this.startCountdown();
    this.cargarImagen(this.message?.id || 0);
  }

  cargarImagen(id: number) {
    this.upSellingService.getImageBlob(id).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        this.imageUrl = this.sanitizer.bypassSecurityTrustUrl(url);
      },
      error: (err) => {
        console.error('Error al cargar la imagen', err);
      }
    });
  }
  
  ngOnDestroy(): void {
    // Limpiar intervalo al destruir el componente.
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  /**
   * Inicia el contador en tiempo real.
   */
  private startCountdown(): void {
    // Actualizar cada segundo
    this.countdownInterval = setInterval(() => {
      if (this.getDaysRemaining() === 0 && this.getHoursRemaining() === 0 &&
        this.getMinutesRemaining() === 0 && this.getSecondsRemaining() === 0) {
        // Promoción expirada, detener contador
        clearInterval(this.countdownInterval);
      }
    }, 1000);
  }

  /**
   * Abre el modal de promoción.
   */
  open(): void {
    if (this.message && this.modalTemplate) {
      this.modalRef = this.modalService.open(this.modalTemplate, {
        size: 'xl',
        centered: true,
        backdrop: 'static',
        keyboard: false,
        scrollable: true
      });
    }
  }

  /**
   * Cierra el modal.
   */
  close(): void {
    this.modalRef?.close();
  }

  /**
   * Formatea la fecha para mostrar en el modal.
   */
  formatDate(date: Date): string {
    if (!date) return '';
    const d = new Date(date);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return d.toLocaleDateString('es-ES', options);
  }

  /**
   * Calcula los días restantes hasta que expire la promoción.
   */
  getDaysRemaining(): number {
    if (!this.message?.fecha_expiracion) return 0;
    const now = new Date();
    const end = new Date(this.message.fecha_expiracion);
    const diff = end.getTime() - now.getTime();
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
  }

  /**
   * Calcula las horas restantes (adicionales a los días).
   */
  getHoursRemaining(): number {
    if (!this.message?.fecha_expiracion) return 0;
    const now = new Date();
    const end = new Date(this.message.fecha_expiracion);
    const diff = end.getTime() - now.getTime();
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    return Math.max(0, hours);
  }

  /**
   * Calcula los minutos restantes (adicionales a las horas).
   */
  getMinutesRemaining(): number {
    if (!this.message?.fecha_expiracion) return 0;
    const now = new Date();
    const end = new Date(this.message.fecha_expiracion);
    const diff = end.getTime() - now.getTime();
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return Math.max(0, minutes);
  }

  /**
   * Calcula los segundos restantes (adicionales a los minutos).
   */
  getSecondsRemaining(): number {
    if (!this.message?.fecha_expiracion) return 0;
    const now = new Date();
    const end = new Date(this.message.fecha_expiracion);
    const diff = end.getTime() - now.getTime();
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return Math.max(0, seconds);
  }

  /**
   * Verifica si la oferta ha expirado.
   */
  isExpired(): boolean {
    if (!this.message?.fecha_expiracion) return false;
    return this.getDaysRemaining() === 0 &&
      this.getHoursRemaining() === 0 &&
      this.getMinutesRemaining() === 0 &&
      this.getSecondsRemaining() === 0;
  }

  /**
   * Evento cuando el usuario acepta la promoción.
   */
  onAcceptPromotion(): void {
    if (this.message) {
      this.acceptPromotion.emit(this.message);
      this.close();
    }
  }

  /**
   * Evento cuando el usuario quiere más información.
   */
  onLearnMore(): void {
    if (this.message) {
      this.learnMore.emit(this.message);
    }
  }

  /**
   * Ejecuta la acción de envío asociada al mensaje de UpSelling.
   */
  onSubmitAction(): void {
    if (!this.message) return;
    switch (this.message.accion) {
      case 'paywall':
        this.openPaywallModal();
        break;
      case 'sales':
        this.openVentasModal();
        break;
      case 'support':
        this.openVentasModal(true);
        break;
      case 'url':
        this.openUrl();
        break;
      default:
        break;
    }
  }

  /**
   * Abre la URL de la promoción en una nueva pestaña.
   */
  openUrl(): void {
    if (!this.message?.url_accion) return;
    window.open(this.message?.url_accion || '', '_blank');
  }

  /**
   * Abre el modal de ventas.
   * @param soporte Indica si el modal es para soporte (true) o ventas (false).
   */
  openVentasModal(soporte: boolean = false): void {
    const modalRef = this.modalService.open(ModalContactoSoporteComponent, { windowClass: 'clear-modal', size: 's', scrollable: true, centered: true });
    modalRef.componentInstance.soloDemo = soporte;
    modalRef.componentInstance.showTutorialButton = false;
    modalRef.result.then((result: any) => {
    }, (reason: any) => { });
  }

  /**
   * Abre el modal de paywall para mostrar los planes disponibles.
   */
  openPaywallModal(): void {
    this.loading.show();
    this.service.planActual().subscribe({
      next: (res: any) => {
        this.loading.hide();
        if (!res.suscripcionVP && !res.suscripcionDLG) {
          this.alerts.addAlert('No posee una suscripción activa. Espere a que finalice su periodo actual para poder suscribirse a través del sistema.', 'info');
          return;
        }
        const modalRef = this.modalService.open(SuscripcionesInformacionPlanesComponent, { size: 'xl', scrollable: true, centered: true, backdrop: 'static' });
        modalRef.componentInstance.suscripcionPrevia = res.suscripcion;
        modalRef.componentInstance.suscripcionVPPrevia = res.suscripcionVP;
        modalRef.componentInstance.suscripcionDLGPrevia = res.suscripcionDLG;
        modalRef.result.then((result: any) => { }, (reason: any) => { });
      },
      error: (err: any) => {
        console.error(err);
        this.loading.hide();
        this.alerts.addAlert("Error al obtener la información.", "danger");
        this.sentryService.captureException(err.error);
      }
    });
  }
}
